/**
 * Cookie Storage Utilities
 * Functions for reading and writing cookie consent to browser cookies
 */

import type { CookiePreferences, StoredConsent } from '../types';
import { COOKIE_CONFIG, DEFAULT_PREFERENCES } from '../constants';

/**
 * Sets a cookie with the given name, value, and expiry days
 */
function setCookie(name: string, value: string, days: number): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax`;
}

/**
 * Gets a cookie value by name
 */
function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const c = cookie.trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length));
    }
  }
  return null;
}

/**
 * Reads the stored consent from cookies
 * @returns The stored consent or null if not found/invalid
 */
export function getStoredConsent(): StoredConsent | null {
  try {
    const cookieValue = getCookie(COOKIE_CONFIG.name);
    if (!cookieValue) {
      return null;
    }

    const parsed = JSON.parse(cookieValue) as StoredConsent;

    // Validate structure
    if (!parsed.preferences || typeof parsed.timestamp !== 'number') {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Saves consent preferences to cookies
 */
export function saveConsent(preferences: CookiePreferences): void {
  const consent: StoredConsent = {
    preferences: {
      ...preferences,
      necessary: true, // Always enforce necessary as true
    },
    timestamp: Date.now(),
    version: COOKIE_CONFIG.version,
  };

  setCookie(COOKIE_CONFIG.name, JSON.stringify(consent), COOKIE_CONFIG.expiryDays);
}

/**
 * Gets current preferences or defaults if no consent stored
 */
export function getPreferences(): CookiePreferences {
  const stored = getStoredConsent();
  if (stored) {
    return stored.preferences;
  }
  return { ...DEFAULT_PREFERENCES };
}

/**
 * Checks if user has given consent (cookie exists)
 */
export function hasConsent(): boolean {
  return getStoredConsent() !== null;
}

/**
 * Clears the consent cookie (for testing purposes)
 */
export function clearConsent(): void {
  document.cookie = `${COOKIE_CONFIG.name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}
